// src/components/tactic/PlaybookPanel.jsx
// Save, reopen, export and import plans. Storage is local to the browser, so
// the export button is what moves a plan to a teammate.

import React, { useRef } from "react";
import { Btn, Label, Panel, Row, T, TextInput } from "./tacticUi";

const when = (iso) => {
  try {
    return new Date(iso).toLocaleString("th-TH", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function PlaybookPanel({
  playbook,
  playbooks,
  dirty,
  onNameChange,
  onNotesChange,
  onSave,
  onNew,
  onLoad,
  onDelete,
  onExport,
  onExportAll,
  onImportFile,
  onExportImage,
  onCopyImage,
  exporting,
}) {
  const fileRef = useRef(null);

  return (
    <Panel
      title="คลังแผน"
      right={
        dirty ? (
          <span style={{ color: T.accent, fontSize: 11 }}>ยังไม่ได้บันทึก</span>
        ) : (
          <span style={{ color: T.faint, fontSize: 11 }}>{playbooks.length} แผน</span>
        )
      }
    >
      <Label>ชื่อแผน</Label>
      <TextInput
        value={playbook.name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="เช่น เปิดเกมยึดสเลเยอร์"
        style={{ marginBottom: 8 }}
      />

      <Label>โน้ตสำหรับสอน</Label>
      <textarea
        value={playbook.notes}
        onChange={(e) => onNotesChange(e.target.value)}
        placeholder="อธิบายจังหวะ เงื่อนไข หรือสิ่งที่ต้องเช็คก่อนเข้า…"
        rows={3}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: "rgba(0,0,0,0.35)",
          border: `1px solid ${T.line}`,
          borderRadius: 8,
          color: T.ink,
          fontFamily: T.font,
          fontSize: 13,
          padding: "8px 10px",
          outline: "none",
          resize: "vertical",
          marginBottom: 10,
        }}
      />

      <Row gap={6} style={{ marginBottom: 10 }}>
        <Btn tone="accent" onClick={onSave} style={{ flex: 1 }}>บันทึก</Btn>
        <Btn onClick={onNew}>แผนใหม่</Btn>
        <Btn onClick={onExport} title="ดาวน์โหลดแผนนี้เป็นไฟล์ .json">ส่งออก</Btn>
      </Row>

      {/* A plan nobody can paste into the team chat is a plan nobody reads. */}
      <Row gap={6} style={{ marginBottom: 10 }}>
        <Btn tone="accent" onClick={onExportImage} disabled={exporting} style={{ flex: 1 }}>
          {exporting ? "กำลังสร้างรูป…" : "⬇ บันทึกรูปกระดาน"}
        </Btn>
        {onCopyImage && (
          <Btn onClick={onCopyImage} disabled={exporting} title="คัดลอกไปวางในแชตได้เลย">
            คัดลอก
          </Btn>
        )}
      </Row>
      <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 12, lineHeight: 1.6 }}>
        ได้ภาพกระดาน ณ เวลาที่หัวอ่านจอดอยู่ พร้อมหมอกและเส้นทางเดิน
      </div>

      <Row gap={6} style={{ marginBottom: 12 }}>
        <Btn tone="ghost" onClick={() => fileRef.current?.click()} style={{ flex: 1 }}>นำเข้าไฟล์แผน</Btn>
        <Btn tone="ghost" onClick={onExportAll} disabled={!playbooks.length}>สำรองทั้งหมด</Btn>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onImportFile(f);
            e.target.value = "";
          }}
        />
      </Row>

      <div style={{ maxHeight: 210, overflowY: "auto", display: "grid", gap: 6 }}>
        {playbooks.map((p) => {
          const isCurrent = p.id === playbook.id;
          return (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 9px",
                borderRadius: 8,
                background: isCurrent ? "rgba(255,16,38,0.10)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${isCurrent ? "rgba(255,16,38,0.4)" : T.line}`,
              }}
            >
              <button
                type="button"
                onClick={() => onLoad(p)}
                style={{
                  flex: 1,
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  color: "inherit",
                  fontFamily: T.font,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: T.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.name}
                </div>
                <div style={{ fontSize: 10.5, color: T.faint }}>
                  {when(p.updatedAt)} · {p.tokens.length} ฮีโร่ ·{" "}
                  {p.ourSide === "blue" ? "เราบลู" : "เราเรด"}
                </div>
              </button>
              <Btn
                tone="danger"
                onClick={() => onDelete(p)}
                style={{ padding: "4px 8px", fontSize: 11 }}
                title="ลบแผนนี้"
              >
                ลบ
              </Btn>
            </div>
          );
        })}
        {!playbooks.length && (
          <div style={{ color: T.faint, fontSize: 12, padding: "8px 0" }}>
            ยังไม่มีแผนที่บันทึกไว้ — กด “บันทึก” เพื่อเก็บแผนแรก
          </div>
        )}
      </div>
    </Panel>
  );
}
